<template>
  <header class="flex flex-wrap border-b border-gray-200 shadow-sm bg-white" v-preline>
    <!-- Desktop Header -->
    <div
      class="hidden w-full md:flex md:items-center md:justify-between container mx-auto px-4 max-w-screen-xl py-3"
    >
      <!-- Logo and Navigation -->
      <div class="flex items-center gap-6">
        <!-- Logo -->
        <router-link to="/" class="flex items-center" @mouseenter="handleHomeHover">
          <img src="@/assets/icons/logo.svg" alt="Logo" class="w-5 h-5" />
          <span class="ml-5 text-sm font-medium text-primary-black">{{
            $t('header.openPool')
          }}</span>
        </router-link>

        <!-- Navigation Links -->
        <NavLinks
          :is-logged-in="isHeaderLoggedIn"
          :current-route="route.path"
          class="hidden md:flex"
        />
      </div>

      <!-- Auth Buttons or User Menu -->
      <div class="flex items-center gap-4">
        <template v-if="!isHeaderLoggedIn">
          <!-- Language Selector for unauthenticated users -->
          <LanguageSelector variant="secondary" size="sm" />
          <router-link to="/login" class="btn-secondary py-[10px] px-3 text-sm font-semibold">
            {{ $t('header.logIn') }}
          </router-link>
          <router-link to="/signup" class="btn-primary py-[10px] px-3 text-sm font-semibold">
            {{ $t('header.signUp') }}
          </router-link>
        </template>
        <template v-else>
          <!-- My Statistic Link -->
          <router-link
            to="/statistics"
            class="text-sm font-medium text-gray-500 hover:text-primary"
            :class="{ 'text-primary font-semibold': isStatisticsActive }"
            @click="handleNavClick"
            @mouseenter="handleStatisticHover"
          >
            {{ $t('header.myStatistic') }}
          </router-link>
          <div class="flex items-center gap-2 py-2 px-4">
            <Wallet class="size-4 text-gray-500" />
            <p class="text-sm font-medium text-gray-500">
              <template v-if="accountOverviewLoading || btcDisplayLoading">
                <span class="animate-pulse">{{ $t('header.loading') }}</span>
              </template>
              <template v-else-if="accountOverview">
                {{ btcDisplayFormatted }}
              </template>
              <template v-else>
                <span class="text-gray-400">0.00000000 BTC</span>
              </template>
            </p>
          </div>
          <!-- User Dropdown -->

          <router-link
            to="/settings"
            class="py-2 px-4 inline-flex items-center gap-x-2 text-sm font-semibold rounded-lg border border-gray-200 bg-white text-black hover:bg-gray-50"
          >
            {{ userName }}
            <div class="border-l sm:border-l-1 h-4 sm:border-s border-secondary-border"></div>
            <Settings class="size-4" />
          </router-link>
        </template>
      </div>
    </div>

    <!-- Mobile Header -->
    <div
      class="flex md:hidden w-full justify-between items-center container mx-auto px-4 max-w-screen-xl py-3"
    >
      <!-- Logo -->
      <router-link to="/" class="flex items-center">
        <img src="@/assets/icons/logo.svg" alt="Logo" class="w-5 h-5" />
        <span class="ml-3 text-sm font-medium text-primary-black hidden sm:block">{{
          isHeaderLoggedIn ? $t('header.dashboard') : $t('header.publicPool')
        }}</span>
      </router-link>

      <!-- Mobile Sign Up button (when not logged in) or Mobile Menu Toggle -->
      <div class="flex items-center gap-3">
        <!-- Language Selector for mobile unauthenticated users -->
        <LanguageSelector v-if="!isHeaderLoggedIn" variant="secondary" size="sm" />
        <router-link
          v-if="!isHeaderLoggedIn"
          to="/signup"
          class="btn-primary py-[8px] px-3 text-sm font-semibold"
        >
          {{ $t('header.signUp') }}
        </router-link>
        <router-link
          v-if="isHeaderLoggedIn"
          to="/settings"
          class="py-2 px-4 inline-flex items-center gap-x-2 text-sm font-semibold rounded-lg border border-gray-200 bg-white text-black hover:bg-gray-50"
        >
          {{ userName }}
          <div class="border-l sm:border-l-1 h-4 sm:border-s border-secondary-border"></div>
          <Settings class="size-4" />
        </router-link>
        <!-- Sidebar Toggle Button -->
        <button
          type="button"
          class="hs-collapse-toggle btn-icon"
          data-hs-overlay="#hs-sidebar-mobile-nav"
          aria-controls="hs-sidebar-mobile-nav"
          :aria-label="$t('header.toggleNavigation')"
        >
          <Menu class="size-4 hs-overlay-open:hidden" />
          <X class="size-4 hidden hs-overlay-open:block" />
        </button>
      </div>
    </div>

    <!-- Mobile Sidebar -->
    <AppSidebar />
  </header>
</template>

<script setup lang="ts">
import AppSidebar from '@/components/common/AppSidebar.vue'
import LanguageSelector from '@/components/ui/LanguageSelector.vue'
import { usePrefetchStrategies } from '@/lib/query-helpers'
import { useAccountOverviewQuery } from '@/queries/useUser'
import { useAuthStore } from '@/stores/authStore'
import { useCurrencyStore } from '@/stores/currencyStore'
import { Menu, Settings, Wallet, X } from 'lucide-vue-next'
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import NavLinks from './NavLinks.vue'

// Define props for demo purposes
const props = withDefaults(
  defineProps<{
    forceLoggedIn?: boolean
    forceLoggedOut?: boolean
  }>(),
  {
    forceLoggedIn: false,
    forceLoggedOut: false
  }
)

const authStore = useAuthStore()
const route = useRoute()
const currencyStore = useCurrencyStore()
const { prefetchUserDashboard, prefetchPoolData } = usePrefetchStrategies()

const userName = computed(() => authStore.user?.username || 'User')

// Determine login state based on props or actual auth state
const isHeaderLoggedIn = computed(() => {
  if (props.forceLoggedIn) return true
  if (props.forceLoggedOut) return false
  return authStore.isAuthenticated
})

// Use centralized currency instead of hardcoded 'BTC'
const { data: accountOverview, isLoading: accountOverviewLoading } = useAccountOverviewQuery()

const btcDisplayFormatted = computed(() => {
  const amount = accountOverview.value?.unpaid_amount || '0.00000000'
  return `${amount} ${currencyStore.selectedCurrency}`
})
const btcDisplayLoading = computed(() => false)

const isStatisticsActive = computed(() => {
  return route.path === '/statistics' || route.path === '/my-statistics' || route.path === '/'
})

const handleNavClick = (event: Event) => {
  const target = event.target as HTMLAnchorElement
  const href = target.getAttribute('href')

  if (href && route.path === href) {
    event.preventDefault()
    return false
  }
}

const handleStatisticHover = () => {
  if (isHeaderLoggedIn.value) {
    prefetchUserDashboard()
  }
}

const handleHomeHover = () => {
  prefetchPoolData()
}
</script>
